import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Availability() {
  const [days, setDays] = useState([]);
  const [labels, setLabels] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [newBlocked, setNewBlocked] = useState({ date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/availability").then((r) => { setDays(r.data.days); setLabels(r.data.labels); setBlocked(r.data.blocked_dates); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateDay = (weekday, field, value) => {
    setDays(days.map((d) => (d.weekday === weekday ? { ...d, [field]: value } : d)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability", { days });
      toast.success("Horários atualizados!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const addBlocked = async (e) => {
    e.preventDefault();
    if (!newBlocked.date) return;
    await api.post("/admin/availability/blocked-dates", newBlocked);
    setNewBlocked({ date: "", reason: "" });
    load();
  };

  const removeBlocked = async (id) => {
    await api.delete(`/admin/availability/blocked-dates/${id}`);
    load();
  };

  return (
    <div data-testid="admin-availability-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Disponibilidade</h1>

      <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 mb-8">
        <p className="text-sm font-medium text-[#9B6179] mb-4">Horário Semanal (sessões de 2 horas)</p>
        <div className="space-y-3">
          {days.map((d) => (
            <div key={d.weekday} className="flex items-center gap-4" data-testid={`availability-day-${d.weekday}`}>
              <Switch checked={d.enabled} onCheckedChange={(v) => updateDay(d.weekday, "enabled", v)} data-testid={`availability-switch-${d.weekday}`} />
              <span className="w-24 text-sm text-[#594A50]">{labels[d.weekday]}</span>
              <Input type="time" disabled={!d.enabled} value={d.start_time} onChange={(e) => updateDay(d.weekday, "start_time", e.target.value)} className="w-32" data-testid={`availability-start-${d.weekday}`} />
              <span className="text-[#6F3F55]">até</span>
              <Input type="time" disabled={!d.enabled} value={d.end_time} onChange={(e) => updateDay(d.weekday, "end_time", e.target.value)} className="w-32" data-testid={`availability-end-${d.weekday}`} />
            </div>
          ))}
        </div>
        <Button disabled={saving} onClick={save} className="mt-6 bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="availability-save-button">Salvar Horários</Button>
      </div>

      <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5">
        <p className="text-sm font-medium text-[#9B6179] mb-4">Datas Bloqueadas (feriados, férias...)</p>
        <form onSubmit={addBlocked} className="flex gap-3 mb-4">
          <Input type="date" value={newBlocked.date} onChange={(e) => setNewBlocked({ ...newBlocked, date: e.target.value })} className="w-48" data-testid="blocked-date-input" />
          <Input placeholder="Motivo (opcional)" value={newBlocked.reason} onChange={(e) => setNewBlocked({ ...newBlocked, reason: e.target.value })} data-testid="blocked-date-reason" />
          <Button type="submit" variant="outline" data-testid="blocked-date-add">Bloquear</Button>
        </form>
        <div className="space-y-2">
          {blocked.map((b) => (
            <div key={b.id} className="flex justify-between items-center text-sm border-b border-[#9B6179]/5 py-2" data-testid={`blocked-date-${b.id}`}>
              <span>{b.date} {b.reason && `— ${b.reason}`}</span>
              <button onClick={() => removeBlocked(b.id)} data-testid={`blocked-date-remove-${b.id}`}><Trash2 className="w-4 h-4 text-[#6F3F55] hover:text-red-600" /></button>
            </div>
          ))}
          {blocked.length === 0 && <p className="text-sm text-[#6F3F55]">Nenhuma data bloqueada.</p>}
        </div>
      </div>
    </div>
  );
}
